import {
  Message,
  MonitoredObjects,
  Request
} from "src/gen/schema/syncfollow/syncfollow";
import { isValidDeviceId } from "src/utils/api/streaming/streamingUtils";
import { generateDataId } from "src/utils/data";

import { MessageManager } from "./core/messageManager";
import {
  ErrorHandler,
  MessageReducer,
  MessageTransformer,
  MetaHandler,
  UpdateHandler
} from "./core/types";
import { BilgeStreamManager } from "./bilgeStreamManager";
import { ConfigDStreamManager } from "./configDStreamManager";
import { SextantStreamManager } from "./sextantStreamManager";
import { StreamManager } from "./streamManager";
import { StreamPool } from "./streamPool";

/**
 * Data source pool like DataSource
 * but with a pool of connections
 */
export class PooledDataSource<TKey, TValue extends StreamManager> {
  private pool: StreamPool<TKey, TValue>;

  constructor(sm: StreamPool<TKey, TValue>) {
    this.pool = sm;
  }

  subscribe<TRequest extends Message, TResponse, TData>(
    fabricId: TKey,
    request: TRequest,
    transform: MessageTransformer<TResponse>,
    reducer: MessageReducer<TResponse>,
    onSuccess: UpdateHandler<TData[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    /**
     * This is an id based on the request converted
     * to a MD5 hash. if there is a duplicate request
     * they will hash out to the same and, we can
     * reuse that stream.
     */
    const groupId = generateDataId(request);
    /**
     * This will either get an existing stream or
     * create a new one.
     */
    const pool = this.pool.useStream(fabricId);
    if (pool) {
      let stream = pool.getStream(groupId);
      /* no stream so add a new one. */
      if (!stream) {
        stream = pool.addStream(request, transform, reducer);
      }
      if (stream) {
        /**
         * stream.subscribe() returns the cleanup
         * function used to unsubscribe.
         */
        return stream.subscribe(onSuccess, onError, onMeta);
      }
    }
    /**
     * Because Typescript... there
     * will always be a stream but
     * typescript says it is possible
     * so need to handle the compiler
     * here.
     */
    return MessageManager.unsubscribe;
  }

  send<TRequest extends Message>(fabricId: TKey, request: TRequest) {
    const pool = this.pool.useStream(fabricId);
    if (pool) {
      pool.sendMessage(request);
    }
  }
}

const filterOutInvalidIds = (ids?: string[]) => {
  if (ids) {
    return ids.filter((id) => isValidDeviceId(id));
  }
  return [];
};

export class SextantDataSource extends PooledDataSource<
  string | undefined,
  SextantStreamManager
> {
  subscribe<TResponse, TData>(
    fabricId: string | undefined,
    message: Message,
    transform: MessageTransformer<TResponse>,
    reducer: MessageReducer<TResponse>,
    onSuccess: UpdateHandler<TData[]>,
    onError: ErrorHandler,
    onMeta?: MetaHandler
  ): () => void {
    /**
     * Make a copy of the request message
     * just because I feel wrong modifying
     * the original message.
     */
    let msg: Message = { ...message };
    /**
     * dig out the 1st monitored object
     * to see if there are any switchIds
     * or objectIds that need to be
     * filtered.
     */
    const original = msg.request?.objects?.[0];
    if (original) {
      if (original.switchId?.length || original.objectId?.length) {
        /**
         * If there are any switchIds or objectIds
         * in the monitored objects, we need to
         * filter out any invalid ids and
         * create a new message with the
         * filtered switchId.
         */
        const switchId = filterOutInvalidIds(original.switchId);
        const objectId = filterOutInvalidIds(original.objectId);

        if (objectId.length + switchId.length === 0) {
          /**
           * if there are no valid switchIds
           * or objectIds after filtering,
           * we can just call the onSuccess
           * callback with an empty array
           * and return the unsubscribe
           * function.
           */
          onSuccess([], false, 0, 0);
          return MessageManager.unsubscribe;
        }

        /**
         * Recreate the Message with the
         * valid switchIds and/or
         * objectIds.
         */
        msg = Message.fromPartial({
          request: Request.fromPartial({
            ...msg?.request,
            objects: [
              MonitoredObjects.create({
                ...original,
                switchId,
                objectId
              })
            ]
          })
        });
      }
    }

    return super.subscribe(
      fabricId,
      msg,
      transform,
      reducer,
      onSuccess,
      onError,
      onMeta
    );
  }
}

export class ConfigDDataSource extends PooledDataSource<
  string | undefined,
  ConfigDStreamManager
> {}

export class BilgeDataSource extends PooledDataSource<
  string | undefined,
  BilgeStreamManager
> {}
